import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, EmptyState, Loading } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATS = ["Signature Rolls", "Poke Bowls", "Ramen", "Starters", "Mains", "Desserts", "Drinks", "Sides"];
const empty = { name: "", description: "", price: "", category: "Mains", available: true };

export default function MenuPage() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/menu").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, price: String(it.price) }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (editing) await api.put(`/menu/${editing.id}`, payload);
      else await api.post("/menu", payload);
      toast.success(editing ? "Item updated" : "Item added");
      setOpen(false);
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this menu item?")) return;
    await api.delete(`/menu/${id}`);
    toast.success("Item deleted");
    load();
  };

  if (!items) return <Loading />;

  const allCats = [...new Set([...CATS, ...items.map((i) => i.category)])];
  const grouped = allCats.map((c) => ({ cat: c, list: items.filter((i) => i.category === c) })).filter((g) => g.list.length);

  return (
    <div data-testid="menu-page">
      <PageHeader
        title="Menu"
        subtitle="Manage your dishes, prices and availability."
        action={<Button data-testid="add-menu-btn" onClick={openNew} className="bg-primary hover:bg-[#B35A42] text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Add item</Button>}
      />

      {items.length === 0 ? (
        <EmptyState icon={UtensilsCrossed} title="No menu items yet" desc="Add your first dish to start building your menu."
          action={<Button onClick={openNew} className="bg-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Add item</Button>} />
      ) : (
        <div className="space-y-8">
          {grouped.map((g) => (
            <div key={g.cat}>
              <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">{g.cat}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {g.list.map((it) => (
                  <div key={it.id} data-testid={`menu-item-${it.id}`} className="bg-card border border-border rounded-xl p-5 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium">{it.name}</p>
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{it.description}</p>
                      </div>
                      <span className="font-heading text-xl font-semibold">${Number(it.price).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      {it.available
                        ? <Badge className="bg-secondary/15 text-secondary hover:bg-secondary/15 border-0">Available</Badge>
                        : <Badge variant="outline" className="text-muted-foreground">Hidden</Badge>}
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(it)} data-testid={`edit-menu-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(it.id)} data-testid={`delete-menu-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit item" : "Add menu item"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label>
              <Input data-testid="menu-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Price ($)</Label>
                <Input data-testid="menu-form-price" type="number" step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="menu-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label>Available on menu</Label>
              <Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="menu-form-submit" className="bg-primary hover:bg-[#B35A42] text-primary-foreground">{editing ? "Save changes" : "Add item"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
